import { DateSelector } from '@popsure/dirty-swan';
import {
  QuestionnaireFormProps,
  QuestionnaireQuestions,
} from '../../../models';
import { useEffect, useState } from 'react';

import { Header } from '../../Screen';

import '../../../../datepicker.scss';
import { DateOperation, calculate } from '../../../utils/DateOperations';

type Overrides = {
  firstDayOfWeek?: number;
};

const localeOverridesMapping: Partial<Record<'en' | 'xx', Overrides>> = {
  en: {},
  xx: {},
};

type DateFormProps<Questionnaire extends QuestionnaireQuestions> = {
  yearRange:
    | { min: number; max: number }
    | { min: DateOperation; max: DateOperation };
  displayCalendar?: boolean;
  placeholders?: {
    day?: string;
    month?: string;
    year?: string;
    error?: string;
    errorAfterMaxYear?: string;
    errorBeforeMinYear?: string;
  };
  dayjsLocale?: ILocale;
  firstDayOfWeek?: number;
} & QuestionnaireFormProps<Questionnaire, string>;

export const DateForm = <Questionnaire extends QuestionnaireQuestions>({
  value,
  yearRange,
  onSubmitValue,
  question,
  setValidForSubmission,
  displayCalendar = true,
  featureName,
  placeholders,
  dayjsLocale,
  firstDayOfWeek = 0,
}: DateFormProps<Questionnaire>) => {
  const allowedLocales = ['en'];

  const [date, setDate] = useState<string | undefined>(value);

  useEffect(() => {
    setValidForSubmission(!!date);
  }, [date, setValidForSubmission]);

  const handleSubmit = () => {
    if (!date) {
      throw new Error(
        `[${featureName}] Date value is missing for submission for question: ${question}`
      );
    }
    onSubmitValue(date);
  };

  const convertedYearRange = {
    min:
      typeof yearRange.min === 'number'
        ? yearRange.min
        : calculate(yearRange.min).year(),
    max:
      typeof yearRange.max === 'number'
        ? yearRange.max
        : calculate(yearRange.max).year(),
  };

  let processedPlaceholders = null;
  if (placeholders) {
    processedPlaceholders = {
      ...placeholders,
      ...(placeholders.errorAfterMaxYear && {
        errorAfterMaxYear: `${placeholders.errorAfterMaxYear} ${convertedYearRange.max}`,
      }),
      ...(placeholders.errorBeforeMinYear && {
        errorBeforeMinYear: `${placeholders.errorBeforeMinYear} ${convertedYearRange.min}`,
      }),
    };
  }

  return (
    <Header onSubmit={handleSubmit}>
      <DateSelector
        value={date}
        yearBoundaries={convertedYearRange}
        onChange={setDate}
        displayCalendar={displayCalendar}
        placeholders={
          processedPlaceholders || {
            day: 'Day',
            month: 'Month',
            year: 'Year',
          }
        }
        dayjsLocale={dayjsLocale}
        firstDayOfWeek={firstDayOfWeek}
        {...(allowedLocales.includes('en') ? localeOverridesMapping['en'] : {})}
      />
    </Header>
  );
};
